import type { PutTripDatesType, PutTripDestinationsType } from '@orbit/api-types'
import { toCompact, toDashed } from '@lib/datetime'
import { Trip } from '@types'
import {
	CreateTripDraft,
	DraftDestination,
	seedDraft,
	totalDays,
} from '@pages/Trips/CreateTripFlow/core/draft'
import { placeFromLocation } from '@pages/Trips/CreateTripFlow/core/place'

/**
 * Pure state for the edit-destinations flow: a create-trip draft seeded from
 * the trip's saved destinations, plus the context needed to map the edited
 * draft back onto the server contract. Mirrors the iOS flow's
 * `EditTripDatesState` so the clients agree on semantics.
 */
export interface EditTripDestinationsState {
	tripId: number
	/**
	 * The trip's full day count when the editor opened — can exceed the days
	 * its destinations cover (pre-destination trips; tripDates stretches).
	 */
	seededDayCount: number
	/** The rows as seeded, in order — what the draft is diffed against. */
	seededDestinations: DraftDestination[]
	/** Dashed YYYY-MM-DD, or null. */
	seededStartDate: string | null
	seededCoveredDays: number
	/**
	 * The days each saved destination owns today, by destination id — what a
	 * shortened or removed row gives up, and where its events live.
	 */
	seededSpans: Record<number, DaySpan>
	draft: CreateTripDraft
}

/** A contiguous run of zero-based trip days. */
export interface DaySpan {
	startDayIndex: number
	dayCount: number
}

/** An event's placement — all the editor needs to know of it. */
export interface EventPlacement {
	tripDayIndex: number
}

/** Seeds the editor from a trip. Trip responses already order destinations by start day. */
export const seedEditState = (trip: Trip): EditTripDestinationsState => {
	const startDate = toDashed(trip.startDate) || null
	const draft = seedDraft(
		trip.destinations.map(destination => ({
			id: destination.id,
			place: placeFromLocation(destination.location),
			dayCount: destination.dayCount,
		})),
		startDate,
	)
	return {
		tripId: trip.id,
		seededDayCount: trip.dayCount,
		seededDestinations: draft.destinations,
		seededStartDate: startDate,
		seededCoveredDays: totalDays(draft),
		seededSpans: Object.fromEntries(
			trip.destinations.map(destination => [
				destination.id,
				{ startDayIndex: destination.startDayIndex, dayCount: destination.dayCount },
			]),
		),
		draft,
	}
}

export const updateDraft = (
	state: EditTripDestinationsState,
	draft: CreateTripDraft,
): EditTripDestinationsState => ({ ...state, draft })

// MARK: Days

/**
 * Trailing trip days no destination owns, as the save would leave them.
 * Lengthening coverage into the uncovered range adopts those days in place
 * (they keep their events); the rest slide to the end and survive. A
 * projection of the server's layout rules — the save response stays the
 * authority on the final count.
 */
export const uncoveredDays = (state: EditTripDestinationsState): number =>
	Math.max(0, state.seededDayCount - Math.max(totalDays(state.draft), state.seededCoveredDays))

/** The full trip length the edit would produce: covered plus uncovered days. */
export const totalTripDays = (state: EditTripDestinationsState): number =>
	totalDays(state.draft) + uncoveredDays(state)

// MARK: Events

const eventsOnDays = (
	events: ReadonlyArray<EventPlacement>,
	from: number,
	to: number,
): number => events.filter(event => event.tripDayIndex >= from && event.tripDayIndex < to).length

/** What a draft row holds and what its edit would strand. */
export interface RowEvents {
	/** Events on the days the row owns today; a row not saved yet holds none. */
	eventCount: number
	/**
	 * Events on the days a shortened row gives up (ADR 0007: shortening drops
	 * its last days). The server refuses the whole edit while any remain.
	 */
	strandedCount: number
}

/** A saved destination the draft dropped while it still holds events — the same refusal, with no row left to say so. */
export interface RemovedRowEvents {
	name: string
	eventCount: number
}

export interface DestinationEvents {
	/** By draft row key. */
	rows: Record<string, RowEvents>
	removed: RemovedRowEvents[]
}

/**
 * Projects the server's rule onto the draft before Save: each row's event
 * count from its saved days, the events a shortened row would strand, and the
 * removed rows still holding events. Reordering moves a row's days whole, so
 * order never changes a count; the save response stays the authority.
 */
export const destinationEvents = (
	state: EditTripDestinationsState,
	events: ReadonlyArray<EventPlacement>,
): DestinationEvents => {
	const rows: Record<string, RowEvents> = {}
	for (const row of state.draft.destinations) {
		const span = row.id === undefined ? undefined : state.seededSpans[row.id]
		if (!span) {
			rows[row.key] = { eventCount: 0, strandedCount: 0 }
			continue
		}
		const end = span.startDayIndex + span.dayCount
		const keptEnd = span.startDayIndex + Math.min(span.dayCount, row.dayCount)
		rows[row.key] = {
			eventCount: eventsOnDays(events, span.startDayIndex, end),
			strandedCount: eventsOnDays(events, keptEnd, end),
		}
	}

	const keptIds = new Set(state.draft.destinations.map(row => row.id))
	const removed = state.seededDestinations.flatMap(row => {
		const span = row.id === undefined ? undefined : state.seededSpans[row.id]
		if (!span || keptIds.has(row.id)) return []
		const eventCount = eventsOnDays(events, span.startDayIndex, span.startDayIndex + span.dayCount)
		return eventCount > 0 ? [{ name: row.place.name, eventCount }] : []
	})

	return { rows, removed }
}

/** True while the draft would strand an event — the save is refused until it is fixed. */
export const hasEventConflicts = (summary: DestinationEvents): boolean =>
	summary.removed.length > 0 || Object.values(summary.rows).some(row => row.strandedCount > 0)

// MARK: Changes

/**
 * True when the trip had destinations when the editor opened — emptying the
 * list is then unsaveable (the PUT needs at least one row).
 */
export const seededHadDestinations = (state: EditTripDestinationsState): boolean =>
	state.seededDestinations.length > 0

/** A row's place never changes once added, so key + days name it fully. */
const rowSignature = (rows: DraftDestination[]): string =>
	rows.map(row => `${row.key}:${row.dayCount}`).join('|')

/**
 * True once the draft differs from what the trip was seeded with — gates both
 * the save CTA and the discard confirmation. Order, days, removals and
 * additions all count; so does the start date.
 */
export const hasChanges = (state: EditTripDestinationsState): boolean =>
	rowSignature(state.draft.destinations) !== rowSignature(state.seededDestinations) ||
	state.draft.startDate !== state.seededStartDate

// MARK: Save

/** The wire call a destinations/dates edit resolves to. */
export type EditSaveRequest =
	| { kind: 'destinations'; body: PutTripDestinationsType }
	| { kind: 'tripDates'; body: PutTripDatesType }

/**
 * The server call this edit maps onto, or null when the draft can't be saved
 * (an emptied destination list, or a dateless legacy trip).
 */
export const saveRequest = (state: EditTripDestinationsState): EditSaveRequest | null => {
	const { draft } = state
	if (draft.destinations.length > 0) {
		const body: PutTripDestinationsType = {
			destinations: draft.destinations.map(row => ({
				...(row.id !== undefined ? { id: row.id } : {}),
				location: row.place.location,
				dayCount: row.dayCount,
			})),
		}
		// The date rides along only when it moved — omitted leaves it alone, null clears it.
		if (draft.startDate !== state.seededStartDate) {
			body.startDate = draft.startDate ? toCompact(draft.startDate) : null
		}
		return { kind: 'destinations', body }
	}
	// No rows to send: the destinations PUT needs at least one. A trip that
	// never had destinations can still move its start date through the legacy
	// tripDates call; one that had them must keep a row.
	if (seededHadDestinations(state) || state.seededDayCount < 1 || !draft.startDate) return null
	return {
		kind: 'tripDates',
		body: { dayCount: state.seededDayCount, startDate: toCompact(draft.startDate) },
	}
}
